<template>
  <component
    :is="cardData.url ? 'a' : 'div'"
    class="disturber-card"
    :href="cardData.url"
    :style="{
      '--color-background': `${cardData.colorBackground}`,
      '--color-text': `${cardData.colorText}`,
      '--color-text-cta': `${cardData.colorTextCta}`,
      '--color-background-cta': `${cardData.colorBackgroundCta}`,
    }"
  >
    <video-player
      v-if="cardData.video"
      class="disturber-card__video"
      :attributes="{
        autoplay: true,
      }"
      blur-background
      :controls="{
        duration: false,
        id: `disturber-card-${cardData.video.id}`,
        playPause: false,
        progressBar: false,
      }"
      :image="cardData.video.preview_image"
      :intersection-observer="{
        enable: true,
        rootMargin: '0px',
        threshold: 0.1,
      }"
      :video="getVideoSource(cardData.video.sources)"
    />

    <responsive-image
      v-else-if="cardData.image.src"
      class="disturber-card__image"
      :image="cardData.image"
      :padding="false"
      :ratio="false"
      sizes="(min-width: 1024px) 25vw, 50vw"
    />

    <div class="disturber-card__content">
      <p
        v-if="cardData.title"
        class="disturber-card__title text-mobile-headlines-h2 text-desktop-headlines-h2"
        v-text="cardData.title"
      />

      <p
        v-if="cardData.subtitle"
        class="disturber-card__subtitle text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-text="cardData.subtitle"
      />

      <div v-if="cardData.promoCode" class="disturber-card__promo">
        <div class="disturber-card__promo-code">
          <span v-text="cardData.promoText" />

          <button
            ref="copyButton"
            class="disturber-card__code"
            type="button"
            :data-code="cardData.promoCode"
            @click.prevent="handleCopy"
          >
            <span v-text="cardData.promoCode" />

            <span
              ref="tooltip"
              class="tooltip text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
              :class="{ [$class('active')]: showTooltip }"
              v-text="$string('general.hero_teaser.tooltip_text')"
            />
          </button>
        </div>
      </div>

      <btn
        v-if="cardData.ctaText"
        class="disturber-card__cta"
        :label="cardData.ctaText"
        modifiers="secondary center"
        size="medium-large"
        :url="cardData.url"
      />
    </div>
  </component>
</template>

<script>
/**
 * Vue: Disturber card (disturber-card)
 * -----------------------------------------------------------------------------
 * Disturber Card - Used as in grid content in plp.
 *
 * @param {Boolean|Object} cardData - data, false shows loading state.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue';

import Btn from '~global/btn/btn.vue';
import ResponsiveImage from '~global/images/responsive-image.vue';

import IconCopy from '~icons/general/copy.svg';

export default {
  name: 'DisturberCard',

  components: {
    Btn,
    IconCopy,
    ResponsiveImage,
    VideoPlayer: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.video-player' */
          '~async/video-player/video-player'
        ),
    }),
  },

  props: {
    cardData: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      showTooltip: false,
    };
  },

  computed: {
    /**
     * Compute if media array has video.
     * @returns {Boolean}
     */
    hasVideo() {
      return this.cardData.video;
    },
  },

  watch: {
    /**
     * Watch has video to load video player when required.
     */
    hasVideo() {
      this.loadVideoPlayer();
    },
  },

  mounted() {
    this.loadVideoPlayer();
  },

  methods: {
    /**
     * Load video player component.
     */
    loadVideoPlayer() {
      if (!this.hasVideo) {
        return;
      }

      this.loadComponent('video-player');
    },

    /**
     * Get video MP4 source object.
     * @param {Array} sources - Video sources.
     * @returns {Object}
     */
    getVideoSource(sources) {
      return sources.find((source) => source.format === 'mp4');
    },

    /**
     * Copy promo code and toggle tooltip.
     */
    handleCopy() {
      const copyButton = this.$refs.copyButton;
      const tooltip = this.$refs.tooltip;

      if (!copyButton) {
        return;
      }

      const code = copyButton.dataset.code;
      navigator.clipboard.writeText(code);

      if (!tooltip) {
        return;
      }

      this.showTooltip = true;

      setTimeout(() => {
        this.showTooltip = false;
      }, this.$timing('message'));
    },
  },
};
</script>

<style lang="scss">
@import './disturber-card';
</style>
